import {
  DEFAULT_NEWSLETTER_CONSENT_TEXT,
  DEFAULT_NEWSLETTER_OPT_IN,
  parseConsentText,
  type NewsletterOptIn,
} from '@quizee/core';
import type { BrandConfig, EmailList } from '../api/types';
import type { Inherited } from '../brand/inheritance';

/**
 * What an interaction needs to know about its brand's newsletter before it can offer one.
 *
 * The signup step lives on the interaction, because offering the newsletter under the local
 * election quiz and not under the flower show one is an editorial decision. Everything else about
 * the newsletter belongs to the masthead: the lists, the wording a reader agrees to, and whether an
 * address is confirmed before it joins. So the editor reads those from the brand and shows them,
 * rather than asking again with controls that would let one interaction contradict the masthead.
 */
export interface NewsletterContext {
  /** The wording a signup gets when it does not write its own, and which masthead answered. */
  readonly consent: Inherited<string>;
  /** What the brand does with an address. Shown here as context, and changed on the brand. */
  readonly optIn: NewsletterOptIn;
  /** Where to go to change either of the two above. */
  readonly brandId: string;
  readonly brandName: string;
  /**
   * The lists this brand offers, for the picker on the signup step.
   *
   * Empty rather than missing while they have not loaded, or if the brand has none, so a signup
   * always has something to fall back to: the default list, which is what an empty picker still
   * offers by naming nothing at all.
   */
  readonly lists: readonly EmailList[];
}

/**
 * Read a brand into what the interaction editor needs.
 *
 * The wording is named as coming from the brand rather than from whichever layer under it
 * actually answered. That is the true statement at this level: from an interaction's point of
 * view the masthead decides, and the masthead's own screen is where the org and the quizee
 * default are told apart. Naming a layer this screen cannot show would send an editor looking for
 * a setting that is not on the page they were sent to.
 *
 * An older reply carries no resolved newsletter wording. Reading that absence as the quizee
 * default is right rather than a guess: an API that does not answer with the field is one that
 * never let anybody set it, so the default really is what that brand resolves to.
 *
 * `lists` is read separately from the brand, through its own endpoint, so it defaults to empty
 * rather than being required: a screen that could not read the lists still offers the signup step,
 * with nothing to pick beyond the brand's default.
 */
export function newsletterContext(
  brand: BrandConfig,
  lists: readonly EmailList[] = [],
): NewsletterContext {
  return {
    consent: {
      value: brand.resolved.newsletter_consent_text ?? DEFAULT_NEWSLETTER_CONSENT_TEXT,
      from: brand.name,
    },
    optIn: brand.effective_newsletter_opt_in ?? DEFAULT_NEWSLETTER_OPT_IN,
    brandId: brand.brand_id,
    brandName: brand.name,
    lists,
  };
}

/** The brand's default list, named, or a plain fallback while the lists have not loaded. */
export function defaultListName(lists: readonly EmailList[]): string {
  return lists.find((list) => list.is_default)?.name ?? 'the default list';
}

/** One list, named, and marked when it is no longer taking new members. */
export function listName(lists: readonly EmailList[], listId: string): string {
  const list = lists.find((item) => item.list_id === listId);
  if (list === undefined) return listId;
  return list.archived ? `${list.name} (archived)` : list.name;
}

/**
 * The wording a signup would actually be published with.
 *
 * An empty string is a value rather than an absence, so it does not fall through to the brand's:
 * an interaction that has deliberately cleared its own wording has cleared it.
 */
export function resolvedSignupConsent(
  override: string | null,
  inherited: string | undefined,
): string {
  if (override !== null) return override;
  return inherited ?? '';
}

/**
 * Whether a signup with this wording would survive publishing.
 *
 * Publishing drops a signup whose wording comes out empty, and it is right to (D30): asking for
 * marketing consent means showing a reader what they are agreeing to and recording those exact
 * words, so with nothing to show there is nothing that could honestly be recorded. The editor
 * checks it with the same parser the publisher uses, so a wording that is only markup counts as
 * empty here exactly as it will there.
 */
export function signupWouldVanish(wording: string): boolean {
  return parseConsentText(wording).length === 0;
}
